import React, { createContext, useEffect, useRef, useState } from 'react';
import { trim } from 'lodash';
import { FullPageLoader } from 'components';
import { Cache, compareBackend, toastMessageServiceSingleton } from 'services';
import { lendingOnboardingSteps, offersFormat, storageKeys, stringFormat } from 'utils';

const initialState = {
  application: {},
  setApplication: () => {},
};

export const LendingContext = createContext(initialState);

export const LendingContextProvider = ({ children }) => {
  const [application, setApplication] = useState({});
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaderPhase, setLoaderPhase] = useState(null);
  const [saveError, setSaveError] = useState(false);
  const [techError, setTechError] = useState(false);

  //  use a ref to hold the application snapshot at the moment the user
  // clicks Next. This avoids the race where the useEffect fires with stale
  // application state before React has committed the setApplication update.
  const pendingApplication = useRef(null);

  useEffect(() => {
    const cachedApp = Cache.fetch(storageKeys.compare_application);
    if (cachedApp) {
      setApplication(cachedApp);
    }
  }, []);

  useEffect(() => {
    if (techError) {
      throw new Error('Technical Error in compare service');
    }
  }, [techError]);

  //  watch only updating. When it flips to true, pendingApplication.current
  // already holds the fully-built app object (set synchronously in updateApplication
  // before setUpdating(true) is called), so we never read stale state.
  useEffect(() => {
    if (!updating || !pendingApplication.current) {
      return;
    }

    const app = pendingApplication.current;
    Cache.save(storageKeys.compare_application, app);

    compareBackend()
      .saveApplication(app)
      .then(async (outcome) => {
        const isSuccessful = outcome?.successful;
        const isTechError = outcome?.techError;

        setSaveError(!isSuccessful);
        setTechError(isTechError);

        if (!isSuccessful) {
          if (!isTechError) {
            toastMessageServiceSingleton.addError(
              outcome.message || 'Credit enquiry failed',
              null,
              'N/A',
              true,
              null,
              true,
            );
          }
          pendingApplication.current = null;
          setUpdating(false);
          return;
        }

        // After personal_details credit check succeeds, run the quotes phase.

        if (app.lastStep === lendingOnboardingSteps.personal_details) {
          setLoaderPhase('quotes');

          const minDelay = new Promise((resolve) => setTimeout(resolve, 2000));

          const [qoutesResponse] = await Promise.all([compareBackend().fetchQuotes(app), minDelay]);

          if (qoutesResponse?.status < 400 && qoutesResponse?.result) {
            const offers = qoutesResponse.result;
            const updateApp = {
              ...app,
              offers: {
                hashId: offers.id,
                matches: offersFormat(offers),
              },
            };
            Cache.save(storageKeys.compare_application, updateApp);
            setApplication(updateApp);
          }

          pendingApplication.current = null;
          setLoaderPhase('done');
          setUpdating(false);
          return;
        }

        // All other steps: finish normally
        pendingApplication.current = null;
        setUpdating(false);
      });
  }, [updating]);

  const uuidv4 = () =>
    ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
    );

  const updateApplication = (step, data) => {
    // build the full next application object synchronously, store it in the ref, THEN call setUpdating(true). This guarantees the useEffect always
    // reads the correct up-to-date application, regardless of React's render timing.
    let nextApp;

    setApplication((prevApp) => {
      switch (step) {
        case lendingOnboardingSteps.get_started:
          nextApp = {
            ...prevApp,
            lastStep: step,
            canSave: false,
            request: {
              ...prevApp?.request,
              applicationMethod: data.applicationMethod,
            },
          };
          break;
        case lendingOnboardingSteps.request:
          nextApp = {
            ...prevApp,
            lastStep: step,
            canSave: false,
            request: {
              ...prevApp?.request,
              loanTerm: data.loanTerm,
              loanAmount: data.loanAmount,
              loanUsage: data.loanUsage,
              incomeSource: data.incomeSource,
              incomeFrequency: data.incomeFrequency,
              grossIncome: trim(data.grossIncome),
              estimatedExpenses: trim(data.estimatedExpenses),
            },
          };
          break;
        case lendingOnboardingSteps.personal_details:
          setLoaderPhase('credit');
          nextApp = {
            ...prevApp,
            lastStep: step,
            canSave: true,
            personal: {
              firstName: trim(data.firstName),
              lastName: trim(data.lastName),
              idNumber: stringFormat.stripWhitespace(data.idNumber.replaceAll('_', '')),
              clientReference: uuidv4(),
              email: trim(data.email),
              mobileNumber: stringFormat.stripWhitespace(data.mobileNumber),
              consent: data.consentToggle,
            },
          };
          break;

        case lendingOnboardingSteps.offers_details:
          nextApp = {
            ...prevApp,
            lastStep: step,
            canSave: true,
            selectedOffer: data,
          };
          break;
        case lendingOnboardingSteps.offers_suggestions:
          nextApp = {
            ...prevApp,
            lastStep: step,
            canSave: false,
          };
          break;
        case lendingOnboardingSteps.thank_you:
          nextApp = {
            ...prevApp,
            lastStep: step,
            canSave: true,
          };
          break;
        default:
          throw new Error(`update profile step not implemented: ${step}`);
      }

      // Store the fully-built app in the ref before the render cycle
      pendingApplication.current = nextApp;
      return nextApp;
    });

    setUpdating(true);
  };

  return (
    <LendingContext.Provider
      value={{
        application,
        setApplication,
        updateApplication,
        updating,
        setUpdating,
        loading,
        setLoading,
        saveError,
        setSaveError,
        loaderPhase,
        setLoaderPhase,
      }}
    >
      <FullPageLoader showLoader={loading || updating} loadingPhase={loaderPhase} />
      {children}
    </LendingContext.Provider>
  );
};
